import { DistrictStreet, CreateDistrictStreetRequest, UpdateDistrictStreetSortCodeRequest } from "@/models/district";
import { assertTokenValidForMutation, handleResponse } from "@/services/service-helpers";
import log from '@/lib/logger';

const NBH_DISTRICT_STREETS_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/district-streets`;
const NBH_DISTRICT_STREETS_ADMIN_BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/district-streets`;

export async function fetchAllDistrictStreets(token: string | null): Promise<DistrictStreet[] | void> {
    if (!token) {
        log.error("No token provided. Cannot fetch district streets.");
        return Promise.reject("No token provided.");
    }

    const response = await fetch(`${NBH_DISTRICT_STREETS_BASE_URL}`, {
        method: "GET",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });

    return handleResponse<DistrictStreet[]>(response);
}

export async function suggestDistrictsForStreet(
    token: string | null,
    street: string
): Promise<DistrictStreet[] | void> {
    if (!token) {
        log.error("No token provided. Cannot suggest districts for street.");
        return Promise.reject("No token provided.");
    }

    const response = await fetch(
        `${NBH_DISTRICT_STREETS_BASE_URL}/suggest?street=${encodeURIComponent(street)}`,
        {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        }
    );

    return handleResponse<DistrictStreet[]>(response);
}

export async function createDistrictStreet(
    token: string | null,
    request: CreateDistrictStreetRequest
): Promise<DistrictStreet | void> {
    if (!token) {
        log.error("No token provided. Cannot create district street mapping.");
        return Promise.reject("No token provided.");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_DISTRICT_STREETS_ADMIN_BASE_URL}`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(request)
    });

    return handleResponse<DistrictStreet>(response);
}

export async function updateDistrictStreetSortCode(
    token: string | null,
    id: string,
    request: UpdateDistrictStreetSortCodeRequest,
): Promise<DistrictStreet | void> {
    if (!token) {
        log.error("No token provided. Cannot update district street sort code.");
        return Promise.reject("No token provided.");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_DISTRICT_STREETS_ADMIN_BASE_URL}/${id}`, {
        method: "PATCH",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(request)
    });

    return handleResponse<DistrictStreet>(response);
}

export async function deleteDistrictStreet(token: string | null, id: string): Promise<void> {
    if (!token) {
        log.error("No token provided. Cannot delete district street mapping.");
        return Promise.reject("No token provided.");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_DISTRICT_STREETS_ADMIN_BASE_URL}/${id}`, {
        method: "DELETE",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        }
    });

    return handleResponse<void>(response);
}
